import { FC } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Widget, WidgetEmptyState, WidgetSimpleList } from '@factorialco/f0-react/dist/experimental'
import {
  ExternalLink as ExternalLinkIcon,
  Pencil as PencilIcon,
} from '@factorialco/f0-react/icons/app'
import {
  CompanyLinksHomeWidgetDocument,
  CompanyLinksHomeWidgetQuery,
} from 'generated/resources/graphql'
import sortBy from 'lodash/sortBy'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { useActivities } from 'modules/core/lib/activities'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import { NEW_HOME_WIDGETS, NEW_HOME_WIDGET_ACTION_TYPES } from 'modules/dashboard/lib/events'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import * as styles from './index.vanilla.css'

const useLinkAndTitle = () => {
  const title = i18n.t('new_dashboard.company_links.title')

  const { openActivity } = useActivities()

  const handleClick = () => {
    track(trackingEvents.dashboard.clickOnHomeWidgetAction, {
      widget: NEW_HOME_WIDGETS.COMPANY_LINKS,
      actionType: NEW_HOME_WIDGET_ACTION_TYPES.UPPER_RIGHT_BUTTON,
    })
    openActivity('core.dashboardCompanyLinksEdit')
  }

  const link = {
    onClick: handleClick,
    title,
    icon: PencilIcon,
  }

  return {
    title,
    link,
  }
}

const prepareData = (data: CompanyLinksHomeWidgetQuery) => {
  const links = sortBy(data.dashboard.companyLinks, 'order').map((link) => ({
    name: link.name,
    url: link.url,
  }))

  return {
    items: links.map((link) => ({
      id: link.name,
      title: link.name,
      url: link.url,
      rightIconClassName: 'text-f1-icon',
      rightIcon: ExternalLinkIcon,
    })),
  }
}

type PreparedData = ReturnType<typeof prepareData>

const CompanyLinksWidgetComponent: FC<{ data: PreparedData; employeeId: number }> = ({
  data: { items },
}) => {
  const { title, link } = useLinkAndTitle()

  const handleClickItem = (itemId: string | number) => {
    track(trackingEvents.dashboard.clickOnHomeWidgetItem, {
      widget: NEW_HOME_WIDGETS.COMPANY_LINKS,
    })

    const item = items.find((it) => it.id === itemId)

    if (!item) return

    window.open(item.url, '_blank')
  }

  const manageLinksPolicy = 'dashboard.company_links.manage'

  const { loading } = useAsyncData(
    () => policyAuthorizations.authorizePolicies(manageLinksPolicy),
    []
  )

  const canEditCompanyLinks = policyAuthorizations.for(manageLinksPolicy)
  const canSeeEditAction = canEditCompanyLinks && !loading

  return (
    <Widget
      header={{
        title,
        link: canSeeEditAction ? link : undefined,
      }}
    >
      <Box className={styles.container}>
        <WidgetSimpleList items={items} onClickItem={handleClickItem} showAllItems />
      </Box>
    </Widget>
  )
}

const EmptyComponent = () => {
  const { link, title } = useLinkAndTitle()

  return (
    <Widget header={{ title, link }}>
      <WidgetEmptyState
        title={i18n.t('new_dashboard.company_links.empty.title')}
        description={i18n.t('new_dashboard.company_links.empty.description')}
        emoji="🔗"
      />
    </Widget>
  )
}

export const buildCompanyLinksWidget = () =>
  buildWidget({
    id: 'company-links',
    document: CompanyLinksHomeWidgetDocument,
    prepareData,
    emptyComponent: () => <EmptyComponent />,
    isEmpty: (data) => !data.items.length,
    component: CompanyLinksWidgetComponent,
  })
